import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Lock, RefreshCw, Trash2, Archive, MailOpen, Reply, Inbox as InboxIcon, Users, Gamepad2, Send, ShieldCheck, ShieldX, KeyRound, Vault } from "lucide-react";
import LiveMark from "@/pages/idevra/LiveMark";
import "@/pages/idevra/idevra.css";

const API = process.env.REACT_APP_BACKEND_URL;
const FILTERS = ["all", "new", "read", "replied", "archived"];
const STATUS_CLS = {
  new: "text-[#D97706]",
  read: "text-[#64748B]",
  replied: "text-[#0284C7]",
  archived: "text-[#94A3B8]",
};

const flagOf = (cc) =>
  cc ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "";

export default function Inbox() {
  const [token, setToken] = useState(() => localStorage.getItem("idevra_inbox_token") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [visitors, setVisitors] = useState(null);
  const [showVisitors, setShowVisitors] = useState(false);
  const [ventures, setVentures] = useState(null);
  const [showVentures, setShowVentures] = useState(false);
  const [vaultReqs, setVaultReqs] = useState(null);
  const [showVault, setShowVault] = useState(false);
  const [digestState, setDigestState] = useState("idle");

  const loadVaultReqs = useCallback(async (tok) => {
    try {
      const res = await fetch(`${API}/api/vault/access/requests`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.ok) setVaultReqs(await res.json());
    } catch {
      /* noop */
    }
  }, []);

  const decideVault = async (id, action) => {
    const res = await fetch(`${API}/api/vault/access/${id}/decide`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action }),
    });
    if (res.status === 401) return lock();
    if (res.ok) {
      const d = await res.json();
      setVaultReqs((rs) => rs.map((r) => (r.id === id ? { ...r, status: d.status, access_key: d.access_key } : r)));
    }
  };

  const delVaultReq = async (id) => {
    if (!window.confirm("Delete this vault access request permanently?")) return;
    const res = await fetch(`${API}/api/vault/access/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) return lock();
    if (res.ok) setVaultReqs((rs) => rs.filter((r) => r.id !== id));
  };

  const sendDigest = async () => {
    if (digestState === "sending") return;
    setDigestState("sending");
    try {
      const res = await fetch(`${API}/api/digest/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) return lock();
      setDigestState(res.ok ? "sent" : "failed");
    } catch {
      setDigestState("failed");
    } finally {
      setTimeout(() => setDigestState("idle"), 3000);
    }
  };

  const loadVentures = useCallback(async (tok) => {
    try {
      const res = await fetch(`${API}/api/ventures/stats`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.ok) setVentures(await res.json());
    } catch {
      /* noop */
    }
  }, []);

  const loadVisitors = useCallback(async (tok) => {
    try {
      const res = await fetch(`${API}/api/visitors/stats`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.ok) setVisitors(await res.json());
    } catch {
      /* noop */
    }
  }, []);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/comms`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error(String(res.status));
      setMsgs(await res.json());
      setAuthed(true);
      setToken(tok);
      localStorage.setItem("idevra_inbox_token", tok);
      loadVisitors(tok);
      loadVentures(tok);
      loadVaultReqs(tok);
      return true;
    } catch {
      setAuthed(false);
      localStorage.removeItem("idevra_inbox_token");
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadVisitors, loadVentures, loadVaultReqs]);

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authorize = async (e) => {
    e.preventDefault();
    setAuthError("");
    const ok = await load(input.trim());
    if (!ok) setAuthError("ACCESS DENIED — INVALID OPERATOR CODE");
  };

  const act = async (id, status) => {
    const res = await fetch(`${API}/api/comms/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.status === 401) return lock();
    if (res.ok) setMsgs((m) => m.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const del = async (id) => {
    if (!window.confirm("Delete this transmission permanently?")) return;
    const res = await fetch(`${API}/api/comms/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) return lock();
    if (res.ok) setMsgs((m) => m.filter((x) => x.id !== id));
  };

  const lock = () => {
    localStorage.removeItem("idevra_inbox_token");
    setAuthed(false);
    setToken("");
    setInput("");
  };

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "all" ? msgs.length : msgs.filter((m) => m.status === f).length;
    return acc;
  }, {});
  const visible = filter === "all" ? msgs : msgs.filter((m) => m.status === filter);

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F6F9] p-6" data-testid="inbox-gate">
        <form onSubmit={authorize} noValidate className="iv-glass flex w-full max-w-sm flex-col items-center gap-5 rounded-2xl p-8">
          <LiveMark className="w-14" />
          <div className="text-center">
            <div className="f-syncopate text-sm tracking-[0.35em] text-[#0F172A]">COMMAND DECK</div>
            <div className="f-mono mt-2 text-[9px] tracking-[0.25em] text-[#94A3B8]">
              RESTRICTED — OPERATOR ACCESS CODE REQUIRED
            </div>
          </div>
          <input
            type="password"
            data-testid="inbox-token-input"
            className="iv-input"
            placeholder="ACCESS CODE"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          {authError && (
            <div className="f-mono text-[9px] tracking-[0.15em] text-[#D97706]" data-testid="inbox-auth-error">
              {authError}
            </div>
          )}
          <button
            type="submit"
            data-testid="inbox-authorize-btn"
            disabled={loading}
            className="f-syncopate w-full rounded-full bg-[#0284C7] px-6 py-3 text-[10px] tracking-[0.3em] text-white transition-colors hover:bg-[#0369A1] disabled:opacity-60"
          >
            {loading ? "VERIFYING…" : "AUTHORIZE"}
          </button>
          <Link to="/" className="f-mono text-[9px] tracking-[0.2em] text-[#94A3B8] hover:text-[#0284C7]">
            ← BACK TO CONSOLE
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F9] px-4 py-8 sm:px-8" data-testid="inbox-page">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="inbox-back-link" className="iv-glass flex h-10 w-10 items-center justify-center rounded-full text-[#475569] hover:text-[#0284C7]">
              <ChevronLeft size={16} />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <InboxIcon size={16} color="#0284C7" />
                <h1 className="f-syncopate text-sm tracking-[0.3em] text-[#0F172A]">COMMS INBOX</h1>
              </div>
              <div className="f-mono mt-1 text-[8px] tracking-[0.25em] text-[#94A3B8]">
                {counts.all} TRANSMISSIONS · {counts.new} NEW
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="inbox-digest-btn"
              onClick={sendDigest}
              title="Email me the telemetry digest now"
              className={`iv-glass flex h-10 items-center gap-2 rounded-full px-4 text-[#475569] transition-colors hover:text-[#0284C7] ${
                digestState === "sent" ? "!text-[#0284C7]" : digestState === "failed" ? "!text-[#D97706]" : ""
              }`}
            >
              <Send size={14} className={digestState === "sending" ? "animate-pulse" : ""} />
              <span className="f-mono hidden text-[8px] tracking-[0.2em] sm:block">
                {digestState === "sending" ? "SENDING…" : digestState === "sent" ? "DIGEST SENT" : digestState === "failed" ? "SEND FAILED" : "EMAIL DIGEST"}
              </span>
            </button>
            <button
              type="button"
              data-testid="inbox-refresh-btn"
              onClick={() => load(token)}
              className="iv-glass flex h-10 w-10 items-center justify-center rounded-full text-[#475569] hover:text-[#0284C7]"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              data-testid="inbox-lock-btn"
              onClick={lock}
              className="iv-glass flex h-10 w-10 items-center justify-center rounded-full text-[#475569] hover:text-[#D97706]"
            >
              <Lock size={15} />
            </button>
          </div>
        </div>

        {visitors && (
          <div className="iv-glass mt-6 rounded-2xl p-5" data-testid="visitor-log">
            <button
              type="button"
              data-testid="visitor-log-toggle"
              onClick={() => setShowVisitors((s) => !s)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <span className="flex items-center gap-2">
                <Users size={15} color="#0284C7" />
                <span className="f-syncopate text-[11px] tracking-[0.3em] text-[#0F172A]">VISITOR LOG</span>
              </span>
              <span className="f-mono text-[9px] tracking-[0.2em] text-[#64748B]">
                {visitors.total_visitors} VISITORS · {visitors.total_visits} VISITS {showVisitors ? "▴" : "▾"}
              </span>
            </button>
            {showVisitors && (
              <>
                {visitors.daily && (
                  <div className="mt-4 rounded-xl border border-white/70 p-4" data-testid="visitor-trend-chart">
                    <div className="f-mono pb-3 text-[8px] tracking-[0.3em] text-[#94A3B8]">LAST 7 DAYS · VISITS</div>
                    <div className="flex h-24 items-end gap-2">
                      {visitors.daily.map((d) => {
                        const max = Math.max(1, ...visitors.daily.map((x) => x.visits));
                        return (
                          <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-1" data-testid={`visitor-trend-bar-${d.date}`}>
                            <span className="f-mono text-[8px] text-[#0284C7]">{d.visits || ""}</span>
                            <div
                              className="w-full max-w-[38px] rounded-t-sm"
                              style={{
                                height: `${Math.max(d.visits ? 8 : 3, (d.visits / max) * 100)}%`,
                                background: d.visits ? "linear-gradient(180deg,#38BDF8,#0284C7)" : "#E2E8F0",
                              }}
                            />
                            <span className="f-mono text-[7px] tracking-[0.1em] text-[#94A3B8]">
                              {new Date(`${d.date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" }).toUpperCase()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {visitors.countries && visitors.countries.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="visitor-countries">
                    <span className="f-mono text-[8px] tracking-[0.3em] text-[#94A3B8]">DOCKING FROM:</span>
                    {visitors.countries.map((c) => (
                      <span
                        key={c.code}
                        data-testid={`visitor-country-${c.code}`}
                        className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#CBD5E1] bg-white/60 px-3 py-1.5 text-[9px] tracking-[0.15em] text-[#475569]"
                      >
                        <span className="text-[13px] leading-none">{flagOf(c.code)}</span>
                        {c.code} × {c.count}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 overflow-hidden rounded-xl border border-white/70">
                {visitors.recent.length === 0 && (
                  <div className="f-mono p-4 text-center text-[9px] tracking-[0.25em] text-[#94A3B8]">
                    NO VISITORS RECORDED YET
                  </div>
                )}
                {visitors.recent.map((v) => (
                  <div
                    key={v.visitor_id}
                    className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-white/60 px-4 py-2.5 last:border-b-0 sm:grid-cols-[8rem_1fr_auto]"
                  >
                    <span className="f-mono flex items-center gap-1.5 text-[9px] text-[#0284C7]">
                      {v.country && <span className="text-[12px] leading-none" title={v.country}>{flagOf(v.country)}</span>}
                      {v.visitor_id.slice(0, 8)}…
                    </span>
                    <span className="hidden truncate f-mono text-[8px] text-[#94A3B8] sm:block">
                      {v.user_agent || "unknown device"}
                    </span>
                    <span className="f-mono text-right text-[8px] text-[#64748B]">
                      {v.visits} VISIT{v.visits === 1 ? "" : "S"} · {v.last_seen ? new Date(v.last_seen).toLocaleString() : ""}
                    </span>
                  </div>
                ))}
                </div>
              </>
            )}
          </div>
        )}

        {ventures && (
          <div className="iv-glass mt-6 rounded-2xl p-5" data-testid="venture-telemetry">
            <button
              type="button"
              data-testid="venture-telemetry-toggle"
              onClick={() => setShowVentures((s) => !s)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <span className="flex items-center gap-2">
                <Gamepad2 size={15} color="#0284C7" />
                <span className="f-syncopate text-[11px] tracking-[0.3em] text-[#0F172A]">VENTURE TELEMETRY</span>
              </span>
              <span className="f-mono text-[9px] tracking-[0.2em] text-[#64748B]">
                {ventures.total_opens} OPENS · {ventures.total_launches} LAUNCHES {showVentures ? "▴" : "▾"}
              </span>
            </button>
            {showVentures && (
              <div className="mt-4 overflow-hidden rounded-xl border border-white/70">
                {ventures.items.map((v, i) => {
                  const max = Math.max(1, ventures.items[0]?.opens || 0);
                  return (
                    <div
                      key={v.slug}
                      data-testid={`venture-stat-row-${v.slug}`}
                      className="grid grid-cols-[1.4rem_7rem_1fr_auto] items-center gap-3 border-b border-white/60 px-4 py-2.5 last:border-b-0"
                    >
                      <span className="f-orbitron text-[10px] font-bold text-[#94A3B8]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="truncate f-orbitron text-[10px] font-bold text-[#0F172A]">{v.name}</span>
                      <div className="iv-stat-track h-1.5">
                        <div
                          className="iv-stat-fill h-full"
                          style={{ width: `${Math.round((v.opens / max) * 100)}%` }}
                        />
                      </div>
                      <span className="f-mono text-right text-[8px] tracking-[0.12em] text-[#64748B]">
                        {v.opens} OPEN{v.opens === 1 ? "" : "S"} · {v.launches} LAUNCH{v.launches === 1 ? "" : "ES"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {vaultReqs && (
          <div className="iv-glass mt-6 rounded-2xl p-5" data-testid="vault-access-admin">
            <button
              type="button"
              data-testid="vault-access-toggle"
              onClick={() => setShowVault((s) => !s)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <span className="flex items-center gap-2">
                <Vault size={15} color="#0284C7" />
                <span className="f-syncopate text-[11px] tracking-[0.3em] text-[#0F172A]">VAULT ACCESS</span>
              </span>
              <span className="f-mono text-[9px] tracking-[0.2em] text-[#64748B]">
                <span className={vaultReqs.filter((r) => r.status === "pending").length ? "text-[#D97706]" : ""}>
                  {vaultReqs.filter((r) => r.status === "pending").length} PENDING
                </span>{" "}
                · {vaultReqs.length} TOTAL {showVault ? "▴" : "▾"}
              </span>
            </button>
            {showVault && (
              <div className="mt-4 flex flex-col gap-3">
                {vaultReqs.length === 0 && (
                  <div className="f-mono rounded-xl border border-white/70 p-4 text-center text-[9px] tracking-[0.25em] text-[#94A3B8]" data-testid="vault-access-empty">
                    NO ACCESS REQUESTS YET
                  </div>
                )}
                {vaultReqs.map((r) => (
                  <div key={r.id} className="rounded-xl border border-white/70 bg-white/40 p-4" data-testid={`vault-req-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="f-orbitron text-[12px] font-bold text-[#0F172A]">{r.name}</div>
                        <div className="f-mono mt-1 text-[9px] tracking-[0.1em] text-[#64748B]">
                          {r.email} · {r.phone}
                        </div>
                        <div className="f-mono mt-0.5 text-[9px] tracking-[0.1em] text-[#64748B]">
                          {r.company} · {r.country}
                          {r.role ? ` · ${r.role}` : ""}
                        </div>
                        {r.found_via && (
                          <div className="f-mono mt-0.5 text-[8px] tracking-[0.1em] text-[#94A3B8]">FOUND VIA: {r.found_via}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <span
                          className={`f-mono text-[9px] tracking-[0.25em] ${
                            r.status === "pending" ? "text-[#D97706]" : r.status === "approved" ? "text-[#0284C7]" : "text-[#DC2626]"
                          }`}
                          data-testid={`vault-req-status-${r.id}`}
                        >
                          ● {r.status.toUpperCase()}
                        </span>
                        <div className="f-mono mt-1 text-[8px] text-[#94A3B8]">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {r.status === "pending" && (
                        <>
                          <button
                            type="button"
                            data-testid={`vault-approve-btn-${r.id}`}
                            onClick={() => decideVault(r.id, "approve")}
                            className="f-mono inline-flex items-center gap-1.5 rounded-full bg-[#0284C7] px-3.5 py-2 text-[8px] tracking-[0.2em] text-white hover:bg-[#0369A1]"
                          >
                            <ShieldCheck size={12} /> APPROVE
                          </button>
                          <button
                            type="button"
                            data-testid={`vault-deny-btn-${r.id}`}
                            onClick={() => decideVault(r.id, "deny")}
                            className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#FCA5A5] px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                          >
                            <ShieldX size={12} /> DENY
                          </button>
                        </>
                      )}
                      {r.status === "approved" && r.access_key && (
                        <span className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#CBD5E1] bg-white/60 px-3.5 py-2 text-[9px] tracking-[0.15em] text-[#0284C7]" data-testid={`vault-key-${r.id}`}>
                          <KeyRound size={12} /> {r.access_key}
                        </span>
                      )}
                      {r.status === "denied" && (
                        <button
                          type="button"
                          data-testid={`vault-reapprove-btn-${r.id}`}
                          onClick={() => decideVault(r.id, "approve")}
                          className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#CBD5E1] px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]"
                        >
                          <ShieldCheck size={12} /> APPROVE INSTEAD
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`vault-del-btn-${r.id}`}
                        onClick={() => delVaultReq(r.id)}
                        className="f-mono ml-auto inline-flex items-center gap-1.5 rounded-full border border-transparent px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#94A3B8] hover:border-[#FCA5A5] hover:text-[#DC2626]"
                      >
                        <Trash2 size={12} /> DELETE
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="inbox-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`inbox-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`f-mono rounded-full border px-4 py-2 text-[9px] tracking-[0.25em] transition-colors ${
                filter === f
                  ? "border-[#0284C7] bg-[#0284C7] text-white"
                  : "border-[#CBD5E1] bg-white/60 text-[#64748B] hover:border-[#38BDF8]"
              }`}
            >
              {f.toUpperCase()} · {counts[f]}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-4">
          {visible.length === 0 && (
            <div className="iv-glass rounded-2xl p-10 text-center f-mono text-[10px] tracking-[0.25em] text-[#94A3B8]" data-testid="inbox-empty">
              NO TRANSMISSIONS ON THIS CHANNEL
            </div>
          )}
          {visible.map((m) => (
            <div key={m.id} className="iv-glass rounded-2xl p-5 sm:p-6" data-testid={`inbox-msg-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="f-orbitron text-sm font-bold text-[#0F172A]">{m.name}</div>
                  <div className="f-mono mt-1 text-[9px] tracking-[0.12em] text-[#64748B]">
                    {m.email}
                    {m.organization ? ` · ${m.organization}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`f-mono text-[9px] tracking-[0.25em] ${STATUS_CLS[m.status] || ""}`}>
                    ● {m.status.toUpperCase()}
                  </span>
                  <div className="f-mono mt-1 text-[8px] text-[#94A3B8]">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                  </div>
                </div>
              </div>
              <p className="mt-4 border-l-2 border-[#38BDF8] pl-4 text-sm leading-relaxed text-[#475569]">{m.message}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {m.status === "new" && (
                  <button type="button" data-testid={`inbox-read-btn-${m.id}`} onClick={() => act(m.id, "read")} className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#CBD5E1] px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]">
                    <MailOpen size={12} /> MARK READ
                  </button>
                )}
                <a href={`mailto:${m.email}?subject=Re: Your IDEVRA transmission`} data-testid={`inbox-reply-link-${m.id}`} onClick={() => act(m.id, "replied")} className="f-mono inline-flex items-center gap-1.5 rounded-full bg-[#0284C7] px-3.5 py-2 text-[8px] tracking-[0.2em] text-white hover:bg-[#0369A1]">
                  <Reply size={12} /> REPLY
                </a>
                {m.status !== "archived" && (
                  <button type="button" data-testid={`inbox-archive-btn-${m.id}`} onClick={() => act(m.id, "archived")} className="f-mono inline-flex items-center gap-1.5 rounded-full border border-[#CBD5E1] px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]">
                    <Archive size={12} /> ARCHIVE
                  </button>
                )}
                <button type="button" data-testid={`inbox-delete-btn-${m.id}`} onClick={() => del(m.id)} className="f-mono ml-auto inline-flex items-center gap-1.5 rounded-full border border-transparent px-3.5 py-2 text-[8px] tracking-[0.2em] text-[#94A3B8] hover:border-[#FCA5A5] hover:text-[#DC2626]">
                  <Trash2 size={12} /> DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
